import { useCallback, useEffect, useState, type ReactNode } from "react";
import { api, ApiError, clearToken } from "../lib/api.js";

/** Fetch hook: loading/error/data + refetch. 401 bounces to the token gate. */
export function useApi<T>(path: string | null): {
  data: T | null; error: string | null; loading: boolean; reload: () => void;
} {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(Boolean(path));
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!path) return;
    let live = true;
    setLoading(true);
    api.get<T>(path)
      .then((d) => { if (live) { setData(d); setError(null); } })
      .catch((e: unknown) => {
        if (!live) return;
        if (e instanceof ApiError && e.status === 401) { clearToken(); window.location.reload(); return; }
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [path, tick]);

  return { data, error, loading, reload };
}

export function Panel(props: { title?: string; alarm?: boolean; children: ReactNode; className?: string; delay?: number }) {
  return (
    <section
      className={`panel reveal ${props.alarm ? "alarm" : ""} ${props.className ?? ""}`}
      style={props.delay ? { animationDelay: `${props.delay}ms` } : undefined}
    >
      {props.title ? <h2>{props.title}</h2> : null}
      {props.children}
    </section>
  );
}

export function Readout(props: { label: string; value: ReactNode; tone?: "green" | "red" | "amber" | "dim"; note?: ReactNode; hero?: boolean }) {
  return (
    <div className={`readout ${props.hero ? "hero-num" : ""}`}>
      <div className="tag">{props.label}</div>
      <div className={`val ${props.tone ?? ""}`}>{props.value}</div>
      {props.note ? <div className="note">{props.note}</div> : null}
    </div>
  );
}

export function Chip(props: { tone?: "green" | "red" | "amber" | "blue"; children: ReactNode }) {
  return <span className={`chip ${props.tone ?? ""}`}>{props.children}</span>;
}

export function Empty(props: { children: ReactNode }) {
  return <div className="empty">{props.children}</div>;
}

export function ErrorNote(props: { error: string }) {
  return <div className="empty" style={{ borderColor: "var(--red-dim)", color: "#ff8392" }}>API ERROR · {props.error}</div>;
}

export function statusTone(s: string): "green" | "red" | "amber" | "blue" | undefined {
  if (["succeeded", "completed", "delivered", "opened", "clicked", "replied", "paying", "active", "live", "sent", "approved", "cache_hit"].includes(s)) return "green";
  if (["failed", "bounced", "rejected", "disqualified", "expired", "canceled"].includes(s)) return "red";
  if (["pending", "queued", "running", "trial", "grace_period", "billing_retry", "edited"].includes(s)) return "amber";
  return undefined;
}

/** Minimal mono bar chart (SVG) — values rendered oldest→newest. */
export function Bars(props: {
  values: number[]; labels?: string[]; height?: number; color?: string; format?: (v: number) => string;
}) {
  const { values } = props;
  const h = props.height ?? 64;
  const color = props.color ?? "var(--red)";
  if (values.length === 0) return <Empty>no data yet</Empty>;
  const max = Math.max(...values, 1);
  const bw = 100 / values.length;
  return (
    <div>
      <svg viewBox={`0 0 100 ${h}`} preserveAspectRatio="none" style={{ width: "100%", height: h, display: "block" }}>
        {values.map((v, i) => {
          const bh = Math.max((v / max) * (h - 4), v > 0 ? 1.5 : 0);
          return (
            <rect key={i} x={i * bw + bw * 0.15} y={h - bh} width={bw * 0.7} height={bh}
              fill={color} opacity={0.45 + 0.55 * (i / Math.max(values.length - 1, 1))} />
          );
        })}
      </svg>
      {props.labels ? (
        <div className="row" style={{ justifyContent: "space-between", marginTop: 4 }}>
          <span className="tag">{props.labels[0]}</span>
          <span className="tag">{props.labels[props.labels.length - 1]}</span>
        </div>
      ) : null}
    </div>
  );
}
